import React from 'react';

import clsx from 'classnames';
import PropTypes from 'prop-types';

import { combineClasses } from 'wanderu-css';

import GridContext from './GridContext';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './Grid.scss';

function Grid(props) {
    const {
        align = 'center',
        autoRows = false,
        classes: classesProp = {},
        className,
        columns = 12,
        columnSpacing = undefined, // these need to be undefined by default
        component: Component = 'div',
        componentProps,
        justify = 'center',
        rowSpacing = undefined, // these need to be undefined by default
        spacing = 2,
        ...otherProps
    } = props;

    const classes = combineClasses(styles, classesProp);

    const context = React.useMemo(() => ({ align, justify }), [align, justify]);

    return (
        <GridContext.Provider value={context}>
            <Component
                className={clsx(
                    classes.root,
                    {
                        [classes.autoRows]: autoRows,
                        [classes[`columns-${columns}`]]: columns !== 12,
                        [classes[`columnSpacing-${columnSpacing}`]]: columnSpacing !== undefined,
                        [classes[`rowSpacing-${rowSpacing}`]]: rowSpacing !== undefined,
                        [classes[`spacing-${spacing}`]]: spacing !== 2,
                    },
                    className,
                )}
                {...otherProps}
                {...componentProps}
            />
        </GridContext.Provider>
    );
}

Grid.propTypes = {
    /**
     * Set the alignment on the component.
     */
    align: PropTypes.oneOf(['start', 'center', 'end']),
    /**
     * Auto-adjusts the height of all grid items to be equal to the tallest sibling
     */
    autoRows: PropTypes.bool,
    /**
     * The content of the component.
     */
    children: PropTypes.node.isRequired,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * The number of columns in the grid.
     */
    columns: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    /**
     * Defines the gutter size of n * the base spacing size between the columns.
     */
    columnSpacing: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: PropTypes.elementType,
    /**
     * The set of components passed down to the root component
     */
    componentProps: PropTypes.object,
    /**
     * Set the justification on the component.
     */
    justify: PropTypes.oneOf(['start', 'center', 'end']),
    /**
     * Defines the gutter size of n * the base spacing size between the rows.
     */
    rowSpacing: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    /**
     * Defines the gutter size of n * the base spacing size between the columns and rows.
     */
    spacing: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
};

export default Grid;
